// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import Link from 'next/link';
import { useEffect, useState } from 'react';
import Button from '../Button';
import * as S from '../../styles/Home.styled';

const useStargazingCountdown = () => {
  const [timeLeft, setTimeLeft] = useState<{ hours: string; minutes: string; seconds: string } | null>(null);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const target = new Date(now);
      target.setHours(21, 0, 0, 0);
      if (target <= now) {
        target.setDate(target.getDate() + 1);
      }
      const diff = Math.max(0, target.getTime() - now.getTime());
      const pad = (value: number) => String(value).padStart(2, '0');
      setTimeLeft({
        hours: pad(Math.floor(diff / 3600000)),
        minutes: pad(Math.floor(diff / 60000) % 60),
        seconds: pad(Math.floor(diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return timeLeft;
};

const StargazingCountdown = () => {
  const timeLeft = useStargazingCountdown();

  if (!timeLeft) {
    return null;
  }

  return (
    <S.Countdown>
      <S.CountdownInner>
        <S.CountdownEyebrow>Tonight&apos;s forecast: clear</S.CountdownEyebrow>
        <S.CountdownLabel>
          The <span>stargazing window</span> opens in
        </S.CountdownLabel>
        <S.CountdownClock aria-label={`${timeLeft.hours} hours ${timeLeft.minutes} minutes ${timeLeft.seconds} seconds`}>
          <S.CountdownUnit>
            <strong>{timeLeft.hours}</strong>
            <span>hrs</span>
          </S.CountdownUnit>
          <S.CountdownSeparator>:</S.CountdownSeparator>
          <S.CountdownUnit>
            <strong>{timeLeft.minutes}</strong>
            <span>min</span>
          </S.CountdownUnit>
          <S.CountdownSeparator>:</S.CountdownSeparator>
          <S.CountdownUnit>
            <strong>{timeLeft.seconds}</strong>
            <span>sec</span>
          </S.CountdownUnit>
        </S.CountdownClock>
        <Button as={Link} href="/?category=telescopes#shop">
          Gear up before dark
        </Button>
      </S.CountdownInner>
    </S.Countdown>
  );
};

export default StargazingCountdown;
